import { creatorbase } from '@round/api';
import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { CellContext } from '@tanstack/react-table';
import { formatPostStatsCellValue } from 'Modules/Plans/Posts/helpers';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import styles from './PostsStatsWithAverageCell.module.css';
import cn from 'classnames';
import { formatDecimalToPercentageString } from '@round/utils';
import { assets } from '@round/whitelabel-ui';

const { ArrowUp } = assets;

export type PostStatWithAverage = {
    value: number | null | undefined;
    average?: number | null | undefined;
    formatValueToPercentage?: boolean;
};

type Row = creatorbase.PublicPost;

type Context<T extends Row> = CellContext<T, PostStatWithAverage>;

const getTableMeta = getTableMetaHelper<BasePostTableMeta<Row>>();

const PostStatsWithAverageCell = <T extends Row>({ row, table, getValue }: Context<T>) => {
    const { isLoading, getAreStatsLoading } = getTableMeta(table);
    const areStatsLoading = getAreStatsLoading ? getAreStatsLoading(row.original) : false;
    const { value, average, formatValueToPercentage } = getValue();
    const formatValue = formatValueToPercentage ? formatDecimalToPercentageString : formatPostStatsCellValue;

    if (isLoading || areStatsLoading) {
        return <Skeleton />;
    }

    if (value === null || value === undefined) {
        return '-';
    }

    const percentDiff = value && average && average > 0 ? Math.round(((value - average) / average) * 100) : null;

    const showComparison = percentDiff !== null && percentDiff !== 0;

    return showComparison ? (
        <div className={styles.container}>
            <div className={styles.views}>{formatValue(value)}</div>

            <div
                className={cn(styles.averageBadge, {
                    [styles.positive]: percentDiff > 20,
                    [styles.negative]: percentDiff < -20,
                    [styles.neutral]: percentDiff <= 35 && percentDiff >= -35,
                })}
            >
                {percentDiff > 0 ? <ArrowUp /> : <ArrowUp className={styles.arrowDown} />}
                {Math.abs(percentDiff)}%
            </div>
        </div>
    ) : (
        formatValue(value)
    );
};

export default PostStatsWithAverageCell;
